/**
 * SelectionManager - Unified selection state management
 *
 * Manages selection state across Gallery, Ideas Dashboard, and Phase 4 Harvest.
 * Uses EXISTING CSS: `.idea-card.selected`, `.gallery-card.selected` (black card style)
 *
 * NO new visual styles added - purely state management.
 * The black card selection visuals (50+ rules) already exist in _cards.css.
 *
 * Usage:
 *   import { SelectionManager, ideasSelection } from '../core/selection-manager.js';
 *
 *   // Use singleton instances:
 *   ideasSelection.toggle('idea-123');
 *   ideasSelection.on('change', ({ selected, count }) => updateUI());
 *
 *   // Or create custom instance:
 *   const mySelection = new SelectionManager('my-context');
 */

export class SelectionManager {
  #selected = new Set();
  #context;
  #listeners = new Map();

  /**
   * @param {string} context - Namespace for events (e.g., 'ideas', 'gallery', 'harvest')
   */
  constructor(context = 'default') {
    this.#context = context;
  }

  // ============ Core Operations ============

  /**
   * Select a single item
   * @param {string} id - Item ID
   * @returns {SelectionManager} - For chaining
   */
  select(id) {
    if (!this.#selected.has(id)) {
      this.#selected.add(id);
      this.#emit('change');
    }
    return this;
  }

  /**
   * Deselect a single item
   * @param {string} id - Item ID
   * @returns {SelectionManager} - For chaining
   */
  deselect(id) {
    if (this.#selected.has(id)) {
      this.#selected.delete(id);
      this.#emit('change');
    }
    return this;
  }

  /**
   * Toggle selection state for an item
   * @param {string} id - Item ID
   * @returns {boolean} - New selection state (true = selected)
   */
  toggle(id) {
    if (this.#selected.has(id)) {
      this.#selected.delete(id);
    } else {
      this.#selected.add(id);
    }
    this.#emit('change');
    return this.#selected.has(id);
  }

  /**
   * Select multiple items at once
   * @param {string[]} ids - Array of item IDs
   * @returns {SelectionManager} - For chaining
   */
  selectAll(ids) {
    const prevSize = this.#selected.size;
    ids.forEach(id => this.#selected.add(id));
    if (this.#selected.size !== prevSize) {
      this.#emit('change');
    }
    return this;
  }

  /**
   * Clear all selections
   * @returns {SelectionManager} - For chaining
   */
  clear() {
    if (this.#selected.size > 0) {
      this.#selected.clear();
      this.#emit('change');
    }
    return this;
  }

  // ============ State Queries ============

  /**
   * Check if an item is selected
   * @param {string} id - Item ID
   * @returns {boolean}
   */
  has(id) {
    return this.#selected.has(id);
  }

  /**
   * Get the number of selected items
   * @returns {number}
   */
  get size() {
    return this.#selected.size;
  }

  /**
   * Check if selection is empty
   * @returns {boolean}
   */
  get isEmpty() {
    return this.#selected.size === 0;
  }

  /**
   * Get array of selected IDs
   * @returns {string[]}
   */
  getSelected() {
    return [...this.#selected];
  }

  /**
   * Get the context name
   * @returns {string}
   */
  get context() {
    return this.#context;
  }

  // ============ Event System ============

  /**
   * Subscribe to selection changes
   * @param {string} event - Event name ('change')
   * @param {Function} callback - Handler function: ({ selected, count, context }) => void
   * @returns {Function} - Unsubscribe function
   */
  on(event, callback) {
    if (!this.#listeners.has(event)) {
      this.#listeners.set(event, new Set());
    }
    this.#listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from selection changes
   * @param {string} event - Event name
   * @param {Function} callback - Handler to remove
   */
  off(event, callback) {
    if (this.#listeners.has(event)) {
      this.#listeners.get(event).delete(callback);
    }
  }

  /**
   * Emit an event to all listeners
   * @param {string} event - Event name
   */
  #emit(event) {
    const data = {
      selected: this.getSelected(),
      count: this.#selected.size,
      context: this.#context
    };

    // Notify local listeners
    if (this.#listeners.has(event)) {
      for (const callback of this.#listeners.get(event)) {
        try {
          callback(data);
        } catch (err) {
          console.error(`[SelectionManager:${this.#context}] Listener error:`, err);
        }
      }
    }

    // Dispatch DOM event for global listeners
    document.dispatchEvent(new CustomEvent(`selection:${event}`, {
      detail: data
    }));
  }

  // ============ DOM Helpers ============

  /**
   * Sync selection state to DOM elements by adding/removing 'selected' class
   * @param {string} selector - CSS selector for cards (e.g., '.idea-card', '.gallery-card')
   * @param {string} idAttr - Data attribute containing the ID (default: 'data-id')
   */
  syncToDOM(selector, idAttr = 'data-id') {
    const cards = document.querySelectorAll(selector);
    cards.forEach(card => {
      const id = card.getAttribute(idAttr);
      if (id) {
        card.classList.toggle('selected', this.has(id));
        // Also sync checkbox if present
        const checkbox = card.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = this.has(id);
      }
    });
  }

  /**
   * Setup click-to-select on cards with event delegation
   * Uses EXISTING exclusion pattern from phase4-harvest.js
   *
   * @param {string} containerSelector - Container element selector
   * @param {string} cardSelector - Card element selector
   * @param {string} idAttr - Data attribute for ID (default: 'data-id')
   * @param {Object} options - Configuration options
   * @param {string[]} options.excludeSelectors - Selectors for elements that shouldn't trigger selection
   * @param {Function} options.onSelect - Callback after selection: (id, isSelected, card) => void
   */
  bindToCards(containerSelector, cardSelector, idAttr = 'data-id', options = {}) {
    const container = document.querySelector(containerSelector);
    if (!container) {
      console.warn(`[SelectionManager:${this.#context}] Container not found: ${containerSelector}`);
      return;
    }

    // EXACT exclusion list from existing phase4-harvest.js implementation
    // These are interactive elements that should NOT trigger selection
    const defaultExclusions = [
      '.btn-canvas-view',              // Canvas detail view button
      '.btn-view-idea-large',          // Large VIEW IDEA button at card bottom
      '.idea-copy-btn',                // Copy dropdown trigger
      '.idea-save-btn',                // Save/unsave button
      '.headline-nav-btn',             // Headline navigation arrows
      '.btn-generate-img-placeholder', // Generate image button
      '.btn-generate-img',             // Generate image button (alt)
      '.btn-img-action',               // Image overlay actions (edit, download)
      '.idea-delete-btn',              // Delete button
      'button',                        // Any other button
      'a',                             // Links
      'input',                         // Checkboxes, inputs
      'video',                         // Video elements (prevent selection on play)
      '.image-actions',                // Image action bar
      '.video-actions',                // Video action bar
      '.gallery-card-btns',            // Gallery card buttons
      '.gallery-checkbox'              // Gallery checkbox wrapper
    ];

    const {
      excludeSelectors = defaultExclusions,
      onSelect = null
    } = options;

    container.addEventListener('click', (e) => {
      // Check if click target or ancestor matches card
      const card = e.target.closest(cardSelector);
      if (!card) return;

      // Exclude interactive elements
      for (const selector of excludeSelectors) {
        if (e.target.closest(selector)) return;
      }

      const id = card.getAttribute(idAttr);
      if (!id) return;

      const isSelected = this.toggle(id);
      card.classList.toggle('selected', isSelected);

      // Update checkbox if present
      const checkbox = card.querySelector('input[type="checkbox"]');
      if (checkbox) checkbox.checked = isSelected;

      // Optional callback
      if (onSelect) onSelect(id, isSelected, card);
    });

    console.log(`[SelectionManager:${this.#context}] Bound to ${containerSelector} ${cardSelector}`);
  }
}

// ============================================================================
// Singleton Instances for Common Contexts
// ============================================================================

/** Selection manager for Gallery page (images/videos) */
export const gallerySelection = new SelectionManager('gallery');

/** Selection manager for Ideas Dashboard */
export const ideasSelection = new SelectionManager('ideas');

/** Selection manager for Phase 4 Harvest */
export const harvestSelection = new SelectionManager('harvest');
